import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Hook to restore scroll position when navigating back.
 * @param {boolean} isDataLoaded - Should be true when the data for the page is fully loaded and rendered.
 * @param {string} scrollKey - A unique key for storing the scroll position (e.g., pathname).
 */
const useScrollRestoration = (isDataLoaded, scrollKey) => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const hasRestored = useRef(false);

  // Reset the ref when pathname changes (moving to a new page)
  useEffect(() => {
    hasRestored.current = false;
  }, [pathname]);

  // Restore scroll position when data is loaded
  useEffect(() => {
    if (!isDataLoaded || hasRestored.current) {
      return;
    }

    if (navigationType === 'POP') {
      const lastClickedAdId = sessionStorage.getItem('lastClickedAdId');
      const savedPosition = sessionStorage.getItem(`scrollPos_${scrollKey || pathname}`);

      // Safari needs more time and a different approach
      // Using multiple RAF calls + setTimeout for better Safari compatibility
      let rafId1, rafId2, rafId3;
      let timeoutId;

      const performScroll = () => {
        // PRIORITY 1: Use saved pixel position (most reliable)
        if (savedPosition) {
          const targetScroll = parseInt(savedPosition, 10);
          window.scrollTo(0, targetScroll);
        }
        // FALLBACK: Try to find the specific ad element only if no saved position
        else if (lastClickedAdId) {
          const element = document.getElementById(lastClickedAdId);
          if (element) {
            const rect = element.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = rect.top + scrollTop - 100; // Scroll to element with 100px offset from top

            window.scrollTo(0, Math.max(0, targetPosition));
          }
        }

        hasRestored.current = true;
        // Clear the lastClickedAdId once used to avoid confusing future navigations
        sessionStorage.removeItem('lastClickedAdId');
      };

      // Triple RAF for Safari + setTimeout fallback
      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          rafId3 = requestAnimationFrame(() => {
            performScroll();
          });
        });
      });

      // Safari fallback: if RAF doesn't work, use setTimeout
      timeoutId = setTimeout(() => {
        if (!hasRestored.current) {
          performScroll();
        }
      }, 100);

      return () => {
        if (rafId1) cancelAnimationFrame(rafId1);
        if (rafId2) cancelAnimationFrame(rafId2);
        if (rafId3) cancelAnimationFrame(rafId3);
        if (timeoutId) clearTimeout(timeoutId);
      };
    } else if (navigationType === 'PUSH' || navigationType === 'REPLACE') {
      window.scrollTo(0, 0);
      hasRestored.current = true;
    }
  }, [isDataLoaded, pathname, scrollKey, navigationType]);
};

export default useScrollRestoration;
